import React from 'react';
import { registrarCuentaBancaria, obtenerCuentasBancarias, eliminarCuentaBancaria, obtenerCentro, editarCuentaBancaria } from '../../services/centroService';
import { X, Plus, Building2, CreditCard, User, FileText, ChevronDown, ChevronUp, Save, Edit } from 'lucide-react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Modal from 'react-modal';

Modal.setAppElement('#root');

const Bancos = () => {
  const [cuentasBancarias, setCuentasBancarias] = React.useState([]);
  const [cuentaExpandida, setCuentaExpandida] = React.useState(null);
  const [modalAbierto, setModalAbierto] = React.useState(false);
  const [modoEdicion, setModoEdicion] = React.useState(false);
  const [cuentaSeleccionada, setCuentaSeleccionada] = React.useState(null);
  const [nuevaCuenta, setNuevaCuenta] = React.useState({
    id_centro: '',
    banco: '',
    tipo_cuenta: '',
    numero_cuenta: '',
    beneficiario: '',
    ruc: ''
  });
  const [errores, setErrores] = React.useState({});
  const [formEnviado, setFormEnviado] = React.useState(false);

  React.useEffect(() => {
    cargarCuentasBancarias();
    cargarDatosCentro();
  }, []);

  const cargarDatosCentro = async () => {
    try {
      const response = await obtenerCentro();
      if (response.success && response.data.length > 0) {
        setNuevaCuenta(prev => ({
          ...prev,
          id_centro: response.data[0].id_centro
        }));
      }
    } catch (error) {
      showErrorToast('Error al cargar datos del centro: ' + error.message);
    }
  };

  const cargarCuentasBancarias = async () => {
    try {
      const response = await obtenerCuentasBancarias();
      if (response.success) {
        if (response.data && response.data.length > 0) {
          setCuentasBancarias(response.data);
        } else {
          showWarningToast('No se han encontrado cuentas bancarias registradas');
          setCuentasBancarias([]);
        }
      }
    } catch (error) {
      showErrorToast('Error al cargar cuentas bancarias: ' + error.message);
    }
  };

  const showSuccessToast = (message) => {
    toast.success(message, {
        position: 'top-right',
        autoClose: 3000,
        style: { 
            marginTop: '10px',
            fontFamily: '"Exo 2", sans-serif',
            fontWeight: 500
        },
        containerId: 'bancos'
    });
  };

  const showErrorToast = (message) => {
    toast.error(message, {
        position: 'top-right',
        autoClose: 3000,
        style: { 
            marginTop: '10px',
            fontFamily: '"Exo 2", sans-serif',
            fontWeight: 500
        },
        containerId: 'bancos'
    });
  };

  const showWarningToast = (message) => {
    toast.warning(message, {
        position: 'top-right',
        autoClose: 3000,
        style: { 
            marginTop: '10px',
            fontFamily: '"Exo 2", sans-serif',
            fontWeight: 500
        },
        containerId: 'bancos'
    });
  };

  const confirmarEliminacion = (idCuenta) => {
    toast.warn(
      <div>
        <p className="font-bold mb-2">¿Está seguro de eliminar esta cuenta bancaria?</p>
        <p className="mb-4">Esta acción no se puede deshacer.</p>
        <div className="flex justify-end space-x-2">
          <button
            onClick={() => toast.dismiss()}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={async () => {
              toast.dismiss();
              try {
                await eliminarCuentaBancaria(idCuenta);
                await cargarCuentasBancarias();
                showSuccessToast('Cuenta bancaria eliminada exitosamente');
              } catch (error) {
                showErrorToast('Error al eliminar la cuenta bancaria: ' + error.message);
              }
            }}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Eliminar
          </button>
        </div>
      </div>,
      {
        position: "top-center",
        autoClose: false,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: false,
        closeButton: false,
        className: "rounded-lg",
        containerId: "bancos"
      }
    );
  };

  const validateCedula = (cedula) => {
    // Verificar que tenga exactamente 10 dígitos
    if (!/^\d{10}$/.test(cedula)) {
      return 'La cédula debe tener exactamente 10 dígitos numéricos';
    }

    // Verificar que no sean todos números iguales
    if (/^(\d)\1{9}$/.test(cedula)) {
      return 'La cédula no puede tener todos los dígitos iguales';
    }

    // Verificar código de provincia (01-24)
    const codigoProvincia = parseInt(cedula.slice(0, 2));
    if (codigoProvincia < 1 || codigoProvincia > 24) {
      return 'El código de provincia debe estar entre 01 y 24';
    }

    // Verificar tercer dígito (debe ser menor a 6 para personas naturales)
    const tercerDigito = parseInt(cedula[2]);
    if (tercerDigito >= 6) {
      return 'El tercer dígito debe ser menor a 6 para personas naturales';
    }

    // Algoritmo de validación del dígito verificador
    const digitoVerificador = parseInt(cedula[9]);
    const coeficientes = [2, 1, 2, 1, 2, 1, 2, 1, 2];
    let suma = 0;
    
    for (let i = 0; i < 9; i++) {
      let resultado = parseInt(cedula[i]) * coeficientes[i];
      if (resultado >= 10) {
        resultado = Math.floor(resultado / 10) + (resultado % 10);
      }
      suma += resultado;
    }

    const residuo = suma % 10;
    let digitoCalculado = residuo === 0 ? 0 : 10 - residuo;

    if (digitoCalculado !== digitoVerificador) {
      return 'La cédula no es válida';
    }

    return null; // Cédula válida
  };

  const validateNumeroCuenta = (numero) => {
    return /^\d+$/.test(numero) ? null : 'El número de cuenta debe contener solo números';
  };

  const validateBeneficiario = (nombre) => {
    return /^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/.test(nombre) ? null : 'El beneficiario debe contener solo letras';
  };

  const validateBanco = (banco) => {
    return banco.trim() ? null : 'El nombre del banco es requerido';
  };

  const validateForm = () => {
    const erroresValidacion = {};

    if (!nuevaCuenta.banco) erroresValidacion.banco = 'El banco es requerido';
    if (!nuevaCuenta.tipo_cuenta) erroresValidacion.tipo_cuenta = 'El tipo de cuenta es requerido';
    if (!nuevaCuenta.numero_cuenta) erroresValidacion.numero_cuenta = 'El número de cuenta es requerido';
    if (!nuevaCuenta.beneficiario) erroresValidacion.beneficiario = 'El beneficiario es requerido';
    if (!nuevaCuenta.ruc) erroresValidacion.ruc = 'La cédula es requerida';

    const bancoError = validateBanco(nuevaCuenta.banco);
    if (bancoError) erroresValidacion.banco = bancoError;

    const numeroCuentaError = validateNumeroCuenta(nuevaCuenta.numero_cuenta);
    if (numeroCuentaError) erroresValidacion.numero_cuenta = numeroCuentaError;

    const beneficiarioError = validateBeneficiario(nuevaCuenta.beneficiario);
    if (beneficiarioError) erroresValidacion.beneficiario = beneficiarioError;

    const cedulaError = validateCedula(nuevaCuenta.ruc);
    if (cedulaError) erroresValidacion.ruc = cedulaError;

    setErrores(erroresValidacion);
    return Object.keys(erroresValidacion).length === 0;
  };

  const agregarCuentaBancaria = async (e) => {
    e.preventDefault();
    setFormEnviado(true);
    if (!validateForm()) {
      return;
    }
    try {
      const response = await registrarCuentaBancaria(nuevaCuenta);
      if (response.data) {
        await cargarCuentasBancarias();
        cerrarModal();
        showSuccessToast('Cuenta bancaria registrada exitosamente');
      }
    } catch (error) {
      showErrorToast('Error al agregar cuenta bancaria: ' + error.message);
    }
  };

  const editarCuentaBancariaHandler = async (e) => {
    e.preventDefault();
    setFormEnviado(true);
    if (!validateForm()) {
      return;
    }
    try {
      const response = await editarCuentaBancaria(cuentaSeleccionada.id_cuenta, nuevaCuenta);
      if (response.data) {
        await cargarCuentasBancarias();
        cerrarModal();
        showSuccessToast('Cuenta bancaria actualizada exitosamente');
      }
    } catch (error) {
      showErrorToast('Error al editar cuenta bancaria: ' + error.message);
    }
  };

  const toggleCuenta = (idCuenta) => {
    if (cuentaExpandida === idCuenta) {
      setCuentaExpandida(null);
    } else {
      setCuentaExpandida(idCuenta);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Validación en tiempo real para campos numéricos
    if (name === 'numero_cuenta' || name === 'ruc') {
      // Solo permite números
      if (!/^\d*$/.test(value)) {
        return; // Si no es número, no actualiza el estado
      }
      
      // Limita la longitud de la cédula a 10 dígitos
      if (name === 'ruc' && value.length > 10) {
        return;
      }
    }

    // Para el campo beneficiario, solo permite letras y espacios
    if (name === 'beneficiario') {
      if (!/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]*$/.test(value)) {
        return;
      }
    }

    setNuevaCuenta(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const abrirModalEdicion = (cuenta) => {
    setModoEdicion(true);
    setCuentaSeleccionada(cuenta);
    setNuevaCuenta({
      id_centro: cuenta.id_centro,
      banco: cuenta.banco,
      tipo_cuenta: cuenta.tipo_cuenta,
      numero_cuenta: cuenta.numero_cuenta,
      beneficiario: cuenta.beneficiario,
      ruc: cuenta.ruc
    });
    setModalAbierto(true);
  };

  const cerrarModal = () => {
    setModalAbierto(false);
    setModoEdicion(false);
    setCuentaSeleccionada(null);
    setFormEnviado(false);
    setErrores({});
    setNuevaCuenta({
      id_centro: nuevaCuenta.id_centro,
      banco: '',
      tipo_cuenta: '',
      numero_cuenta: '',
      beneficiario: '',
      ruc: ''
    });
  };

  return (
    <div className="max-w-5xl mx-auto p-6">
      <ToastContainer 
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        className="mt-16"
        limit={3}
        enableMultiContainer
        containerId="bancos"
        style={{ zIndex: 9999 }}
      />
      <h3 className="text-2xl font-light text-gray-800 mb-8">Cuentas Bancarias</h3>
      <div className="grid gap-4">
        {cuentasBancarias.map((cuenta) => (
          <div 
            key={cuenta.id_cuenta} 
            className="bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-300"
          >
            <div 
              className="p-4 cursor-pointer flex items-center justify-between"
              onClick={() => toggleCuenta(cuenta.id_cuenta)}
            >
              <div className="flex items-center space-x-3">
                <Building2 className="text-indigo-500 w-5 h-5" />
                <p className="text-sm font-medium text-gray-700">{cuenta.banco}</p>
              </div>
              {cuentaExpandida === cuenta.id_cuenta ? 
                <ChevronUp className="w-5 h-5 text-gray-400" /> : 
                <ChevronDown className="w-5 h-5 text-gray-400" />
              }
            </div>

            {cuentaExpandida === cuenta.id_cuenta && (
              <div className="px-4 pb-4 border-t border-gray-100">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mt-4">
                  <div className="flex items-center space-x-3">
                    <CreditCard className="text-indigo-500 w-5 h-5 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-gray-400">Cuenta {cuenta.tipo_cuenta}</p>
                      <p className="text-sm text-gray-700 truncate">{cuenta.numero_cuenta}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <User className="text-indigo-500 w-5 h-5 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-gray-400">Beneficiario</p>
                      <p className="text-sm text-gray-700 truncate">{cuenta.beneficiario}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <FileText className="text-indigo-500 w-5 h-5 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-gray-400">Cédula</p>
                      <p className="text-sm text-gray-700 truncate">{cuenta.ruc}</p>
                    </div>
                  </div>
                  <div className="flex justify-end sm:col-span-3 gap-2">
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        abrirModalEdicion(cuenta);
                      }}
                      variant="ghost"
                      size="sm"
                      className="text-indigo-500 hover:bg-indigo-50"
                    >
                      <Edit size={16} className="mr-1" />
                      Editar
                    </Button>
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        confirmarEliminacion(cuenta.id_cuenta);
                      }}
                      variant="ghost"
                      size="sm"
                      className="text-rose-500 hover:bg-rose-50"
                    >
                      <X size={16} className="mr-1" />
                      Eliminar
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      
      <Button 
        onClick={() => setModalAbierto(true)}
        variant="primary"
        size="lg"
        className="mt-6 sm:mt-8 w-full"
      >
        <Plus size={20} className="mr-2" />
        Agregar Nueva Cuenta Bancaria
      </Button>

      <Modal
        isOpen={modalAbierto}
        onRequestClose={cerrarModal}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        overlayClassName="fixed inset-0 bg-black/40 backdrop-blur-sm"
        contentLabel="Gestionar Cuenta Bancaria"
      >
        <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 w-full max-w-lg mx-auto max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg sm:text-xl font-medium text-gray-900 flex items-center">
              <Building2 className="mr-3 text-indigo-500" size={24} />
              {modoEdicion ? 'Editar Cuenta Bancaria' : 'Agregar Cuenta Bancaria'}
            </h2>
            <Button 
              onClick={cerrarModal}
              variant="ghost"
              size="icon"
              className="hover:bg-gray-100 rounded-full"
            >
              <X size={20} />
            </Button>
          </div>

          <form onSubmit={modoEdicion ? editarCuentaBancariaHandler : agregarCuentaBancaria} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Banco
                </label>
                <select
                  name="banco"
                  value={nuevaCuenta.banco}
                  onChange={handleInputChange}
                  className={`w-full rounded-lg border ${formEnviado && errores.banco ? 'border-rose-500' : 'border-gray-300'} bg-white py-2 px-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500`}
                  required
                >
                  <option value="">Seleccione un banco</option>
                  <option value="Banco Pichincha">Banco Pichincha</option>
                  <option value="Banco Guayaquil">Banco Guayaquil</option>
                  <option value="Banco del Pacífico">Banco del Pacífico</option>
                </select>
                {formEnviado && errores.banco && (
                  <p className="mt-1 text-xs text-rose-500">{errores.banco}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo de Cuenta
                </label>
                <select
                  name="tipo_cuenta"
                  value={nuevaCuenta.tipo_cuenta}
                  onChange={handleInputChange}
                  className={`w-full rounded-lg border ${formEnviado && errores.tipo_cuenta ? 'border-rose-500' : 'border-gray-300'} bg-white py-2 px-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500`}
                  required
                >
                  <option value="">Seleccione tipo</option>
                  <option value="Corriente">Cuenta Corriente</option>
                  <option value="Ahorros">Cuenta de Ahorros</option>
                </select>
                {formEnviado && errores.tipo_cuenta && (
                  <p className="mt-1 text-xs text-rose-500">{errores.tipo_cuenta}</p>
                )}
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Número de Cuenta
                </label>
                <input
                  type="number"
                  name="numero_cuenta"
                  value={nuevaCuenta.numero_cuenta}
                  onChange={handleInputChange}
                  placeholder="Ej: 2203456789"
                  className={`w-full rounded-lg border ${formEnviado && errores.numero_cuenta ? 'border-rose-500' : 'border-gray-300'} bg-white py-2 px-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500`}
                  required
                />
                {formEnviado && errores.numero_cuenta && (
                  <p className="mt-1 text-xs text-rose-500">{errores.numero_cuenta}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Beneficiario
                </label>
                <input
                  type="text"
                  name="beneficiario"
                  value={nuevaCuenta.beneficiario}
                  onChange={handleInputChange}
                  placeholder="Ej: Juan Pérez"
                  className={`w-full rounded-lg border ${formEnviado && errores.beneficiario ? 'border-rose-500' : 'border-gray-300'} bg-white py-2 px-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500`}
                  required
                />
                {formEnviado && errores.beneficiario && (
                  <p className="mt-1 text-xs text-rose-500">{errores.beneficiario}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cédula
                </label>
                <input
                  type="number"
                  name="ruc"
                  value={nuevaCuenta.ruc}
                  onChange={handleInputChange}
                  placeholder="Ej: 1234567890"
                  className={`w-full rounded-lg border ${formEnviado && errores.ruc ? 'border-rose-500' : 'border-gray-300'} bg-white py-2 px-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500`}
                  required
                />
                {formEnviado && errores.ruc && (
                  <p className="mt-1 text-xs text-rose-500">{errores.ruc}</p>
                )}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row justify-end gap-3 mt-6">
              <Button
                type="button"
                variant="ghost"
                onClick={cerrarModal}
                className="w-full sm:w-auto"
              >
                Cancelar
              </Button>
              <Button 
                type="submit" 
                variant="primary"
                className="w-full sm:w-auto"
              >
                <Save className="mr-2" size={16} />
                {modoEdicion ? 'Guardar Cambios' : 'Guardar'}
              </Button>
            </div>
          </form>
        </div>
      </Modal>
    </div>
  );
};

const Button = ({ children, onClick, className = '', variant = 'primary', size = 'default', type }) => {
  const baseStyles = 'rounded-lg font-medium transition-all duration-300 flex items-center justify-center';
  
  const variantStyles = {
    primary: 'bg-indigo-500 text-white hover:bg-indigo-600 shadow-sm',
    ghost: 'bg-transparent hover:bg-gray-50',
    green: 'bg-emerald-500 text-white hover:bg-emerald-600 shadow-sm',
    danger: 'bg-rose-500 text-white hover:bg-rose-600 shadow-sm'
  };

  const sizeStyles = {
    default: 'px-4 py-2 text-sm',
    sm: 'px-3 py-2 text-sm',
    lg: 'px-6 py-3 text-base',
    icon: 'p-2',
    xs: 'p-1.5 text-xs'
  };

  return (
    <button
      type={type}
      onClick={onClick}
      className={`
        ${baseStyles} 
        ${variantStyles[variant]} 
        ${sizeStyles[size]} 
        ${className}
      `}
    >
      {children}
    </button>
  );
};

export default Bancos;
